import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { PersonaQuestion } from 'api/cms';
import { Box } from 'components/Box';
import RadioButtons from 'components/Radio/RadioButtons';
import { H2, HeadingSmall } from 'components/Text';
import { colors } from 'theme';
import { imageUrl } from 'utils/filestack';
import { retinaBackgroundImage } from 'utils/style';

const filestackTransform = (width: number, height: number) =>
  `resize=width:${width},height:${height},fit:clip,align:center/quality=value:70`;

const TitleImage = styled(Box)<{ $handle: string; $mimetype: string }>`
  background-size: cover;
  background-position: center;
  ${({ $handle: handle, $mimetype: mimetype }) =>
    retinaBackgroundImage(
      imageUrl({
        handle,
        mimetype,
        transform: filestackTransform(900, 500),
      }),
      {
        filename2x: imageUrl({
          handle,
          mimetype,
          transform: filestackTransform(900, 500),
        }),
        include3x: false,
      }
    )};
`;

type Props = {
  blockIndex?: number;
  question: PersonaQuestion;
  index: number;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateScore?: (...args: Array<any>) => any;
  answered?: boolean;
};

export function PersonaQuizBlockQuestion({
  blockIndex,
  question: { title, image, answers },
  index,
  updateScore,
  answered,
}: Props) {
  const options = useMemo(
    () =>
      answers.map(({ title: label }, value) => ({
        label,
        value,
      })),
    [answers]
  );
  const [value, setValue] = useState(null);

  const onChange = (newValue) => {
    const newIndex = parseInt(newValue, 10);
    setValue(newIndex);

    if (updateScore) {
      updateScore(index, answers[newIndex].points);
    }
  };

  return (
    <Box
      data-qa-id={`cms-persona-quiz-question-${index}`}
      mt={index > 0 && [32, null, 72]}
      opacity={answered ? 0.7 : 1}
    >
      <HeadingSmall mb={18}>
        <Box
          display="inline-block"
          bg={colors.platinum}
          color={colors.battleshipGrey}
          borderRadius="3px"
          px="8px"
        >
          {`${getText('Question')} ${index + 1}`}
        </Box>
      </HeadingSmall>
      <Box
        position="relative"
        width={1}
        overflow="hidden"
        borderRadius={16}
        textAlign="center"
      >
        {image && (
          <TitleImage
            data-qa-id="question-image"
            $handle={image.handle}
            $mimetype={image.mimetype}
            position="absolute"
            top={0}
            left={0}
            width={1}
            height="100%"
          />
        )}
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={1}
          minHeight={['56.25vw', null, null, 500]}
          p={[24, null, null, 64]}
          bg="rgba(0,0,0,0.4)"
          color="white"
        >
          <H2
            analyticsData={
              blockIndex
                ? {
                    eventName: 'CMS Block',
                    eventProperties: {
                      block_position: blockIndex,
                      block_type: 'Persona Quiz',
                    },
                  }
                : {}
            }
            data-qa-id="question-title"
            styleAs={title.length > 80 ? 'h2' : 'h1'}
            my={0}
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
        </Box>
      </Box>
      <Box mt={36} textAlign="left">
        <RadioButtons
          large
          hideRadioButtons
          buttons={options}
          value={value}
          data-qa-id="question-answers"
          onChange={onChange}
          columns={2}
          disabled={answered}
        />
      </Box>
    </Box>
  );
}
